import React, { useCallback, useState } from 'react';
import Select from 'react-select';
import CensusMap, { formatNumber, sumValues } from '../Common/CensusMap';
import { useCensusFiltersContext } from '../Common/Filters/hooks/useCensusFiltersContext';

function variation(from, to) {
    if (from === null || to === null || !from) return '—';
    const percent = (100 * (to - from)) / from;
    return `${percent > 0 ? '+' : ''}${percent.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/**
 * Projeção do município distribuída pelas áreas do mapa na proporção da população de cada uma no
 * Censo 2022 (a projeção só existe para o município: setores de 2010 e 2022 têm códigos diferentes).
 * Todas as áreas crescem no mesmo ritmo, então o padrão de cores é o de 2022; mudam os números.
 */
const ProjectionMap = ({ projection }) => {
    const { filters } = useCensusFiltersContext();
    const { base, currentYear, series } = projection;

    const options = series
        .filter(point => point.year >= base.t2)
        .map(point => ({
            value: point.year,
            label: `${point.year} (${point.kind === 'census' ? 'Censo' : 'projeção'})`,
            population: point.population
        }));
    const [chosen, setChosen] = useState(null);
    const selected = chosen || options.find(option => option.value === currentYear) || options[0];

    // Fator do ano sobre o Censo 2022; null quando a projeção do ano não se aplica (reta <= 0).
    const factor = selected.population === null ? null : selected.population / base.p2;
    const project = useCallback(
        residents => (residents === null || residents === undefined || factor === null ? null : Math.round(residents * factor)),
        [factor]
    );
    const valueOf = useCallback(tract => project(tract.residents), [project]);

    return (
        <CensusMap
            cityId={filters.cidade?.value}
            year={filters.year?.value}
            valueOf={valueOf}
            metricLabel="pessoas"
            toolbar={
                <>
                    <label className="censo-toolbar-label" htmlFor="censo-projection-year">
                        Ano
                    </label>
                    <Select
                        inputId="censo-projection-year"
                        classNamePrefix="select"
                        options={options}
                        value={selected}
                        onChange={setChosen}
                        isSearchable={false}
                        // Menu num portal: abre por cima do mapa (Leaflet usa z-index até 1000).
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: styles => ({ ...styles, zIndex: 1100 }) }}
                    />
                </>
            }
            renderPanel={selection => {
                const residents2022 = sumValues(selection.tracts.map(tract => tract.residents));
                const projected = project(residents2022);
                return (
                    <table className="table table-sm">
                        <tbody>
                            <tr>
                                <td>População em {base.t2} (Censo)</td>
                                <td className="text-right">{formatNumber(residents2022)}</td>
                            </tr>
                            <tr>
                                <td>
                                    {selected.value === base.t2
                                        ? `População em ${base.t2}`
                                        : `Projeção para ${selected.value}`}
                                </td>
                                <td className="text-right">{formatNumber(projected)}</td>
                            </tr>
                            <tr>
                                <td>Variação sobre {base.t2}</td>
                                <td className="text-right">{variation(residents2022, projected)}</td>
                            </tr>
                        </tbody>
                    </table>
                );
            }}
        />
    );
};

export default ProjectionMap;
